<template>
  <b-container fluid>
    <div v-if="userObject && userObject.isSysAdmin">
      <admin-global-overview
        v-if="loaded"
        class="mt-4"
        :flat-groups="flatGroups"
      ></admin-global-overview>

      <b-button
        v-if="loaded"
        class="mt-3"
        variant="outline-primary"
        :to="{ name: 'AdminSnippets' }"
      >
        Snippets
      </b-button>

      <div v-if="loaded" class="mt-3 admin-list-wrapper">
        <admin-account-table
          :accounts="accounts"
          @account-selected="openAccount"
        ></admin-account-table>
      </div>

      <b-modal
        v-model="modalOpen"
        size="xl"
        scrollable
        hide-header
        hide-footer
        modal-class="admin-account-modal"
        dialog-class="admin-account-dialog"
        content-class="admin-account-content"
        body-class="admin-account-body"
        @hidden="onModalHidden"
      >
        <button
          type="button"
          class="admin-modal-close"
          :aria-label="$t('webmag.cancel')"
          @click="modalOpen = false"
        >
          <b-icon icon="x" scale="1.4" aria-hidden="true"></b-icon>
        </button>
        <admin-account-details
          v-if="selectedGroup"
          :group="selectedGroup"
        ></admin-account-details>
      </b-modal>
    </div>
    <div v-else-if="userObject" class="pt-5">
      <p class="text-center" v-html="$t('adminAccountManagement.notAllowed')"></p>
    </div>
  </b-container>
</template>

<script>
import SetLoadingIndicator from '@/graphQlQueries/mutations/setLoadingIndicatorInCache';
import SetPageTypeInCache from '@/graphQlQueries/mutations/setPageTypeInCache';
import SetRunBreadcrumbQueryInCache from '@/graphQlQueries/mutations/setRunBreadcrumbQueryInCache';
import SetShowHeaderInCache from '@/graphQlQueries/mutations/setShowHeaderInCache';
import GetAllAccountsForAdmin from '@/graphQlQueries/queries/getAllAccountsForAdmin';
import UserDataAndAccess from '@/mixins/userDataAndAccess';

function maxTimestampMs(maxBlock) {
  if (!maxBlock) return null;
  const u = maxBlock.updated_at ? new Date(maxBlock.updated_at).getTime() : 0;
  const p = maxBlock.published_at ? new Date(maxBlock.published_at).getTime() : 0;
  const ts = Math.max(u, p);
  return ts > 0 ? ts : null;
}

export default {
  name: 'Admin',
  components: {
    AdminGlobalOverview: () => import('@/components/settings/admin/AdminGlobalOverview.vue'),
    AdminAccountTable: () => import('@/components/settings/admin/AdminAccountTable.vue'),
    AdminAccountDetails: () => import('@/components/settings/admin/AdminAccountDetails.vue'),
  },
  mixins: [UserDataAndAccess],
  data: () => ({
    pageType: 'admin-settings',
    loaded: false,
    flatGroups: [],
    accounts: [],
    selectedGroup: null,
    modalOpen: false,
  }),
  created() {
    this.$apollo.mutate({
      mutation: SetLoadingIndicator,
      variables: { isIndicatorLoading: true },
    });
    this.$apollo.mutate({
      mutation: SetPageTypeInCache,
      variables: { type: 'admin-settings' },
    });
    this.$apollo.mutate({
      mutation: SetRunBreadcrumbQueryInCache,
      variables: { runQuery: true },
    });
    this.$apollo.mutate({
      mutation: SetShowHeaderInCache,
      variables: { show: true },
    });
  },
  apollo: {
    rawAccounts: {
      query: GetAllAccountsForAdmin,
      update(data) {
        if (!data?.groups) return [];

        const flatGroups = data.groups.map((g) => ({
          group_id: g.group_id,
          parent_group_id: g.parent_group_id,
          name: g.name,
          slug: g.slug,
          domain: g.domain,
          group_path: g.group_path,
          settings: g.settings || {},
          created_at: g.created_at,
          page_count: g.pages_aggregate?.aggregate?.count ?? 0,
          project_count: g.projects_aggregate?.aggregate?.count ?? 0,
          last_activity_ms: maxTimestampMs(g.pages_aggregate?.aggregate?.max),
        }));
        this.flatGroups = flatGroups;
        this.accounts = this.buildAccountRows(flatGroups);
        this.loaded = true;

        this.$apollo.mutate({
          mutation: SetLoadingIndicator,
          variables: { isIndicatorLoading: false },
        });
        return data.groups;
      },
      skip() {
        return !this.userObject?.userId || !this.userObject?.isSysAdmin;
      },
      fetchPolicy: 'network-only',
    },
  },
  methods: {
    buildAccountRows(flatGroups) {
      const byId = new Map();
      flatGroups.forEach((g) => byId.set(g.group_id, g));

      // Identify "rootless" groups (parent missing or pointing outside the set).
      // If there is exactly one, treat it as the implicit Webmag super-root and
      // exclude it from the table — its counts are platform-wide and confusing.
      const rootless = flatGroups.filter((g) => !byId.has(g.parent_group_id));
      const excludeIds = new Set(
        rootless.length === 1 ? [rootless[0].group_id] : [],
      );

      return flatGroups
        .filter((g) => !excludeIds.has(g.group_id))
        .map((g) => {
          const parent = byId.get(g.parent_group_id);
          return {
            ...g,
            parent_name: parent && !excludeIds.has(parent.group_id)
              ? parent.name
              : null,
          };
        });
    },
    openAccount(account) {
      this.selectedGroup = account;
      this.modalOpen = true;
    },
    onModalHidden() {
      // Defer clearing so the closing animation doesn't show an empty body.
      setTimeout(() => {
        if (!this.modalOpen) this.selectedGroup = null;
      }, 200);
    },
  },
};
</script>

<style scoped lang="scss">
.admin-list-wrapper {
  padding-bottom: 32px;
}
</style>

<style lang="scss">
.admin-account-modal {
  .admin-account-content {
    background-color: #3a3d40;
    color: #d8d8d8;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 6px;
    overflow: hidden;
  }

  .admin-account-body {
    background-color: #3a3d40;
    color: #d8d8d8;
    padding: 24px 16px 24px;
    position: relative;
  }

  .admin-modal-close {
    position: sticky;
    top: 0;
    float: right;
    z-index: 5;
    margin-top: -8px;
    margin-right: -4px;
    margin-bottom: -32px;
    padding: 4px 8px;
    background-color: transparent;
    border: none;
    color: #b9b9b9;
    line-height: 1;
    cursor: pointer;
    border-radius: 4px;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover,
    &:focus {
      background-color: rgba(255, 255, 255, 0.08);
      color: #fff;
      outline: none;
    }
  }

  // Native scrollbar styling for the scrollable body (WebKit + Firefox).
  .admin-account-body {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
  }
  .admin-account-body::-webkit-scrollbar {
    width: 8px;
  }
  .admin-account-body::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }
  .admin-account-body::-webkit-scrollbar-track {
    background-color: transparent;
  }
}
</style>
